import { useCallback, useEffect, useState } from 'react'

import { api } from '../api'
import { DEAL_ICON } from '../components/DealsStrip'
import { t } from '../i18n'
import { confirmAction, haptic, notify, useBackButton } from '../telegram'
import type { AdminDeal, DealInput } from '../types'

interface Props {
  onClose: () => void
}

const CATEGORIES = ['palas', 'zapatillas', 'pelotas', 'ropa', 'accesorios']

interface Draft {
  url: string
  title: string
  shop: string
  image_url: string
  price: string
  old_price: string
  category: string
  expires: string // YYYY-MM-DD или пусто
  active: boolean
}

const EMPTY: Draft = { url: '', title: '', shop: '', image_url: '', price: '', old_price: '', category: 'palas', expires: '', active: true }

function money(raw: string): number | null {
  const value = Number(raw.replace(',', '.').trim())
  return raw.trim() !== '' && Number.isFinite(value) && value > 0 ? value : null
}

function toDraft(d: AdminDeal): Draft {
  return {
    url: d.url,
    title: d.title,
    shop: d.shop,
    image_url: d.image_url ?? '',
    price: String(d.price),
    old_price: d.old_price != null ? String(d.old_price) : '',
    category: d.category,
    expires: d.expires_at ? d.expires_at.slice(0, 10) : '',
    active: d.active,
  }
}

/** Экран администратора: скидки для тестовой полосы в ленте и переходы по ним. */
export default function AdminDeals({ onClose }: Props) {
  const [deals, setDeals] = useState<AdminDeal[] | null>(null)
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [busy, setBusy] = useState(false)

  useEffect(() => useBackButton(editing !== null ? () => setEditing(null) : onClose), [editing, onClose])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  const load = useCallback(async () => {
    try {
      setDeals(await api.adminDeals())
    } catch (e) {
      setDeals([])
      notify(e instanceof Error ? e.message : 'Ошибка')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }))

  async function fill() {
    if (!draft.url.trim()) return
    setBusy(true)
    try {
      const p = await api.adminPreviewDeal(draft.url.trim())
      set({
        title: p.title ?? draft.title,
        shop: p.shop ?? draft.shop,
        image_url: p.image_url ?? draft.image_url,
        price: p.price != null ? String(p.price) : draft.price,
        old_price: p.old_price != null ? String(p.old_price) : draft.old_price,
      })
      haptic()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    const price = money(draft.price)
    if (price === null || !draft.title.trim() || !draft.shop.trim() || !draft.url.trim()) {
      return notify(`${t().adminDealUrl}, ${t().adminDealTitle}, ${t().adminDealShop}, ${t().adminDealPrice}`)
    }
    const body: DealInput = {
      url: draft.url.trim(),
      title: draft.title.trim(),
      shop: draft.shop.trim(),
      image_url: draft.image_url.trim() || null,
      price,
      old_price: money(draft.old_price),
      category: draft.category,
      expires_at: draft.expires ? new Date(`${draft.expires}T23:59:00`).toISOString() : null,
      active: draft.active,
    }
    setBusy(true)
    try {
      await api.adminSaveDeal(body, editing === 'new' || editing === null ? undefined : editing)
      haptic('medium')
      setEditing(null)
      await load()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setBusy(false)
    }
  }

  async function remove(id: number) {
    if (!(await confirmAction(t().adminDealDeleteConfirm))) return
    try {
      await api.adminDeleteDeal(id)
      setEditing(null)
      await load()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    }
  }

  return (
    <div className="chat-screen" role="dialog" aria-modal="true" aria-label={t().adminDeals}>
      <div className="chat-head">
        <button className="ghost chat-back" onClick={editing !== null ? () => setEditing(null) : onClose} aria-label={t().doneClose}>
          ←
        </button>
        <div className="title chat-title">🏷 {t().adminDeals}</div>
      </div>

      <div className="chat-list">
        {editing === null ? (
          <>
            <div className="muted small" style={{ marginBottom: 10 }}>
              {t().adminDealHint}
            </div>
            <button
              style={{ width: '100%', marginBottom: 12 }}
              onClick={() => {
                setDraft(EMPTY)
                setEditing('new')
              }}
            >
              {t().adminDealNew}
            </button>
            {deals === null && <p className="muted">{t().loading}</p>}
            {deals?.map((d) => (
              <button
                key={d.id}
                className="admin-head"
                style={{ opacity: d.active ? 1 : 0.55 }}
                onClick={() => {
                  setDraft(toDraft(d))
                  setEditing(d.id)
                }}
              >
                <span className="avatar" style={{ marginLeft: 0, fontSize: 18 }}>
                  {DEAL_ICON[d.category] ?? '🎾'}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, display: 'block' }}>{d.title}</span>
                  <span className="muted small" style={{ display: 'block' }}>
                    {d.shop} · {d.price} €{d.discount != null ? ` (−${d.discount}%)` : ''}
                  </span>
                </span>
                <span className="badge neutral">{t().adminDealClicks(d.clicks, d.people)}</span>
              </button>
            ))}
          </>
        ) : (
          <div className="deal-form">
            <label style={{ marginTop: 0 }}>{t().adminDealUrl}</label>
            <input type="url" inputMode="url" value={draft.url} placeholder="https://…" onChange={(e) => set({ url: e.target.value })} />
            <button className="ghost" style={{ width: '100%', marginTop: 8 }} disabled={busy || !draft.url.trim()} onClick={fill}>
              {t().adminDealFill}
            </button>

            <label>{t().adminDealTitle}</label>
            <input value={draft.title} maxLength={160} onChange={(e) => set({ title: e.target.value })} />
            <label>{t().adminDealShop}</label>
            <input value={draft.shop} maxLength={64} onChange={(e) => set({ shop: e.target.value })} />
            <div className="row" style={{ gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label>{t().adminDealPrice}</label>
                <input inputMode="decimal" value={draft.price} onChange={(e) => set({ price: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label>{t().adminDealOld}</label>
                <input inputMode="decimal" value={draft.old_price} onChange={(e) => set({ old_price: e.target.value })} />
              </div>
            </div>
            <label>{t().adminDealImage}</label>
            <input type="url" value={draft.image_url} placeholder="https://…" onChange={(e) => set({ image_url: e.target.value })} />
            {draft.image_url && (
              <div className="deal-img" style={{ marginTop: 8, width: 140 }}>
                <img src={draft.image_url} alt="" referrerPolicy="no-referrer" />
              </div>
            )}
            <label>{t().adminDealCategory}</label>
            <div className="chips">
              {CATEGORIES.map((c) => (
                <button key={c} className="chip" aria-pressed={draft.category === c} onClick={() => set({ category: c })}>
                  {DEAL_ICON[c]} {t().dealCategories[c]}
                </button>
              ))}
            </div>
            <label>{t().adminDealExpires}</label>
            <input type="date" value={draft.expires} onChange={(e) => set({ expires: e.target.value })} />
            <label>
              <span className="row" style={{ gap: 10 }}>
                <input type="checkbox" style={{ width: 20, flex: '0 0 auto' }} checked={draft.active} onChange={(e) => set({ active: e.target.checked })} />
                {t().adminDealActive}
              </span>
            </label>
            <button style={{ width: '100%', marginTop: 14 }} disabled={busy} onClick={save}>
              {t().save}
            </button>
            {typeof editing === 'number' && (
              <button className="danger" style={{ width: '100%', marginTop: 8 }} onClick={() => remove(editing)}>
                {t().adminDealDelete}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
